import type { CSSProperties, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { InterventionIcon } from '../../api/generated';
import {
  INTERVENTION_ICON_LABEL_KEYS,
  INTERVENTION_ICON_OPTIONS
} from '../../constants/interventionIconConstants';
import { InterventionIconComponent } from './InterventionIcon';
import styles from './InterventionIconPickerPanel.module.css';

interface InterventionIconPickerPanelProps {
  isOpen: boolean;
  panelStyle: CSSProperties;
  panelRef: RefObject<HTMLDivElement | null>;
  value?: InterventionIcon;
  onSelect: (icon?: InterventionIcon) => void;
}

export const InterventionIconPickerPanel = ({
  isOpen,
  panelStyle,
  panelRef,
  value,
  onSelect
}: InterventionIconPickerPanelProps) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={styles.panel}
      style={panelStyle}
    >
      <div className={styles.grid}>
        {INTERVENTION_ICON_OPTIONS.map((icon) => (
          <button
            key={icon}
            type="button"
            className={`${styles.option} ${value === icon ? styles.optionActive : ''}`}
            onClick={() => onSelect(icon)}
            title={t(INTERVENTION_ICON_LABEL_KEYS[icon])}
            aria-label={t(INTERVENTION_ICON_LABEL_KEYS[icon])}
          >
            <InterventionIconComponent icon={icon} width={18} height={18} />
          </button>
        ))}
        {/* Empty option, always last: clears the icon (no glyph stored). */}
        <button
          type="button"
          className={`${styles.option} ${styles.optionEmpty} ${value === undefined ? styles.optionActive : ''}`}
          onClick={() => onSelect()}
          title={t('config-icon-none')}
          aria-label={t('config-icon-none')}
        />
      </div>
    </div>,
    document.body
  );
};
